import { initializePuzzle } from '../utils/initializePuzzle';
import { syncPiecesFromHistory } from '../utils/placeFromHistory';
import { getCurrentTheme, toggleTheme, THEME, initializeTheme, setCustomColor } from '../utils/theme/themeManager';
import { startDragging, stopDragging, cancelDragging, reserveFromRemote, releaseFromRemote, checkPieceProximity, placePieceFromRemote, _placePiece, rotateDraggingPiece } from '../utils/piecePlacement';
import { getFunnyNickname } from '../../../utils/formatter';
import { calculateOnePiece, recalculateLayout } from '../utils/layoutManager';


const createPuzzleSlice = (set, get, ref) => {

  // --- Ссылка для хранения состояния и методов ---
  ref.puzzle = {

    config: {
      user: {
        puzzleBackgroundColor: null,
        fullscreen: false,
        trayHeight: 0.30, // Доля высоты окна для трея
        trayGapFactor: 0.55, 

        puzzleSetCat: 'user',
        startAnimation: true,

        touchDragOffsetY: 60, // Смещение перетаскиваемого куска при касании

      },
      game: {
        puzzlePieceCount: 35,
        puzzleType: 'jigsaw',
        puzzlePreviewOpacity: 0.23, // Прозрачность фона-подсказки на доске
        puzzleJitter: 4,
        puzzleTabSize: 38,
        snapToleranceFactor: 0.5, // % от ширины/высоты куска для примагничивания
        pieceRotation: false, // Включено ли вращение
      },

      titleHeight: 40,

      touchDragOffsetDelay: 100, 
      generatorTabScaleFactor: 1,
      baseTabSizePercent: 0.45,

      trayPaddingFactor: 0.5, 

      ghostOpacity: 0.15, 
      disableClipPathTest: false,
      reserveTimeout: 60 * 1000, 
      highlightTimeout: 2500, 
      ghostCleanup: 0.1, 
      isDarkTheme: false, // Флаг текущей темы

      isTouchDevice: ('ontouchstart' in window || (window.navigator.maxTouchPoints > 0 && window.navigator.msMaxTouchPoints > 0)),

      compensation: {
        // Коэффициенты для компенсации расхождений DOM/Canvas (если нужны)
        trayYpx: 0,
      },
    },

    state: {
      resetTime: 0,
      isInitialized: false,
      isLoading: false,
      puzzleImage: null, // ArrayBuffer изображения
      file_id: null, // ID файла изображения
      imageDimensions: { width: 0, height: 0 }, 
      boardDimensions: { width: 0, height: 0 }, 
      imageScale: 1, 
      pieceBaseDimensions: { width: 0, height: 0 }, 
      rows: 0,
      cols: 0,
      pieces: {}, 
      placedPiecesCount: 0, 
      draggingPiece: null, 
      trayScrollTop: 0, 
      bgUrl: null, 
      dimensionsCalculated: null, 
      puzzleImageElement: null, 

    },

    methods: {},

    // --- Структуры для хранения данных канвасов ---
    boardCanvas: { // Данные для канваса доски
        element: null,
        context: null,
        rect: null, // getBoundingClientRect()
        dimensions: { width: 0, height: 0 }, // Логические размеры (теперь равны state.boardDimensions)

        layout: {
          x: 0,
          y: 0,
          width: 0,
          height: 0,
        }
    },

    domRefs: {
    },
    // -------------------------------------------

    boardTransform: { scale: 1, x: 0, y: 0 }, // Масштаб и смещение

    zoom: {
      isZooming: false,
      panThresholdMet: false,
      zoomThresholdMet: false,
      initialDistance: 0,
      initialCenterClient: { x: 0, y: 0 },
      initialCenterContainer: { x: 0, y: 0 },
      initialBoardTransform: { scale: 1, x: 0, y: 0 },

      debug: {
        touches: [],
      }
    },

    // --- Состояние для стартовой анимации ---
    startAnimation: {
        finished: false,
    },
    // ---------------------------------------------

    temp: {
      canvasPosition: { x: 0, y: 0 },
    }

  };


  const config = ref.puzzle.config;

  ref.puzzle.layout = {
    boardContainer: {
      top: 0,
      left: 0,
      width: window.innerWidth,
      height: window.innerHeight * (1 - config.user.trayHeight - config.titleHeight),
    },
    trayContainer: {
      width: window.innerWidth,
      height: window.innerHeight * config.user.trayHeight - config.titleHeight,
    },
  }

  // --- Методы ---
  ref.puzzle.methods = {
    startDragging,
    stopDragging,
    cancelDragging,
    reserveFromRemote,
    releaseFromRemote,
    checkPieceProximity,
    placePieceFromRemote,
    _placePiece,
    rotateDraggingPiece,

    recalculateLayout,
    calculateOnePiece,

    render: () => {
      set({ puzzleRender: Date.now() });
    },
    redraw: (options = {}) => {
      set({
        puzzleRedraw: Date.now(),
        puzzleRender: Date.now(),
        redrawOptions: options,
      });
    },
    layout: () => {
      set({ updateLayout: Date.now() });
    },

    setBoardTransform: (transform) => {

      const scaleWasChanged = ref.puzzle.boardTransform.scale !== transform.scale;

      ref.puzzle.boardTransform = transform;
      ref.puzzle.boardCanvas.rect = ref.puzzle.boardCanvas.element.getBoundingClientRect();

      if (scaleWasChanged) {
        ref.puzzle.methods.recalculateLayout();
        ref.puzzle.methods.redraw({ lite: true });
        // ref.puzzle.methods.render();
        // ref.puzzle.methods.redraw({ threshold: 1000 });
      } else {
        ref.puzzle.methods.render();
      }
    },

    initTheme: () => {
      initializeTheme();
      if (ref.puzzle.config.user.puzzleBackgroundColor) {
        setCustomColor(ref.puzzle.config.user.puzzleBackgroundColor);
      }
      ref.puzzle.config.isDarkTheme = getCurrentTheme() === THEME.DARK;
      ref.puzzle.methods.render();
    },

    toggleTheme: (color) => {
      // Переключаем тему и получаем новую активную тему
      if (color) {
        setCustomColor(color);
      } else {
        ref.work.methods.saveUserSets({ puzzleBackgroundColor: null });
        ref.puzzle.config.puzzleBackgroundColor = null;

        const newTheme = toggleTheme();
        // Обновляем состояние
        ref.puzzle.config.isDarkTheme = newTheme === THEME.DARK;
      }

      window.croco?.appleHaptic?.('light');
      ref.puzzle.methods.render();
      set({ updateLayout: Date.now() });
    },

    initializePuzzle: () => {
      initializePuzzle({ ref, set });
    },

    syncPiecesFromHistory: () => {

      if (
        ref.puzzle.config.user.startAnimation 
        && !ref.puzzle.config.showTray
      ) {

        setTimeout(() => {
          ref.puzzle.config.showTray = 1;
          syncPiecesFromHistory({ ref });
          ref.puzzle.methods.redraw();
        }, 2000);

      } else {

        ref.puzzle.config.showTray = Date.now();
        syncPiecesFromHistory({ ref });
        ref.puzzle.methods.redraw();

      }

    },


    setTrayScroll: (scrollData) => {
        ref.puzzle.state.trayScrollTop = scrollData.scrollTop;
        set({
             puzzleRender: Date.now(),
             redrawOptions: { lite: true }
        });
    },

    autoPlacePercentage: (targetPercentage) => {
        const { puzzle } = ref;
        const { pieces, rows, cols } = puzzle.state;
        if (!puzzle.state.isInitialized || !rows || !cols) return;
        const totalPieces = rows * cols;
        if (totalPieces === 0) return;
        const targetPlacedCount = Math.round(totalPieces * (targetPercentage / 100));
        const currentPlacedCount = Object.values(pieces).filter(p => p.placed).length;
        let piecesToPlaceCount = targetPlacedCount - currentPlacedCount;
        if (piecesToPlaceCount <= 0) return;
        const unplacedPieces = Object.values(pieces).filter(p => !p.placed);
        for (let i = unplacedPieces.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [unplacedPieces[i], unplacedPieces[j]] = [unplacedPieces[j], unplacedPieces[i]];
        }
        const piecesToPlaceNow = unplacedPieces.slice(0, piecesToPlaceCount);
        let actuallyPlacedCount = 0;
        piecesToPlaceNow.forEach(piece => {
            if (puzzle.methods._placePiece(piece.id)) { actuallyPlacedCount++; }
        });
        puzzle.methods.redraw();
        window.croco?.log(`Auto-placed ${actuallyPlacedCount} pieces. Total placed: ${puzzle.state.placedPiecesCount}`);
        ref.puzzle.methods.clearTrayGhosts();
    },

    autoPlaceAlmostAll: () => {
       const { puzzle } = ref;
       const { pieces, rows, cols } = puzzle.state;
       if (!puzzle.state.isInitialized || !rows || !cols) return;
       const unplacedPieces = Object.values(pieces).filter(p => !p.placed);
       if (unplacedPieces.length <= 1) return;
       for (let i = unplacedPieces.length - 1; i > 0; i--) {
           const j = Math.floor(Math.random() * (i + 1));
           [unplacedPieces[i], unplacedPieces[j]] = [unplacedPieces[j], unplacedPieces[i]];
       }
       const piecesToPlaceNow = unplacedPieces.slice(1);
       let actuallyPlacedCount = 0;
       piecesToPlaceNow.forEach(piece => {
          if (puzzle.methods._placePiece(piece.id)) { actuallyPlacedCount++; }
       });
       puzzle.methods.redraw();
       window.croco?.log(`Auto-placed almost all pieces. Actually placed: ${actuallyPlacedCount}. Total placed: ${puzzle.state.placedPiecesCount}`);
       ref.puzzle.methods.clearTrayGhosts();
    },

    resetPlacement: () => {

        const { puzzle } = ref;
        const { pieces } = puzzle.state;
        if (!puzzle.state.isInitialized) return;
        let resetCount = 0;
        Object.values(pieces).forEach(piece => {
            if (piece.placed || piece.onBoard) {
                piece.placed = false; piece.onBoard = false; piece.inTray = true;
                piece.currentPos = null; piece.placedBy = null; piece.placedAt = null;
                piece.highlighted = 0; piece.rotation = 0; piece.visible = true;
                piece.zIndex = 10;
                resetCount++;
            }
        });
        puzzle.state.placedPiecesCount = 0;
        puzzle.state.draggingPiece = null;
        puzzle.state.trayScrollTop = 0;
        window.croco?.appleHaptic?.('heavy');

        ref.puzzle.methods.recalculateLayout();
        ref.puzzle.methods.redraw();

        window.croco?.log(`Reset placement for ${resetCount} pieces.`);
    },

    checkCompletion: () => {
        const { puzzle } = ref;
        const { pieces, isInitialized } = puzzle.state;
        if (!isInitialized) return;
        const totalPieces = Object.keys(pieces).length;
        if (totalPieces === 0) return;
        const placedCount = Object.values(pieces).filter(p => p.placed).length;
        if (placedCount === totalPieces) {
          setTimeout(() => {
              const currentPlacedCount = Object.values(ref.puzzle.state.pieces).filter(p => p.placed).length;
              if (currentPlacedCount === totalPieces) {
                  ref.puzzle.methods.showResults();
              }
          }, 500);
        }
    },

    showResults: () => {

        const funnyNicknames = window.t('funny_nicknames', { returnObjects: true });

        const { puzzle, info, work, menu, telegram } = ref;
        if (menu.isPopupVisible && menu.popupContent?.title === window.t('puzzle.results.title')) return;
        const pieces = puzzle.state.pieces;
        const results = {};
        Object.values(pieces).forEach(piece => {
            if (piece.placed && piece.placedBy) {
                results[piece.id] = { placedBy: piece.placedBy, placedAt: piece.placedAt };
            }
        });
        const counts = {};
        Object.values(pieces).forEach(piece => {
            if (piece.placed && piece.placedBy) {
                counts[piece.placedBy] = (counts[piece.placedBy] || 0) + 1;
            }
        });
        const winners = Object.entries(counts)
            .sort(([, countA], [, countB]) => countB - countA)
            .map(([userId, count]) => ({
                userId: parseInt(userId, 10),
                count,
                name: info.game?.users?.[userId]?.first_name || getFunnyNickname(userId, funnyNicknames)
            }));
        const medals = ['🥇', '🥈', '🥉'];
        const winnerLines = winners.map((winner, index) => {
          const place = medals[index] || `${index + 1}.`;
          const isCurrentUser = winner.userId === telegram.activeUserId;
          const nameDisplay = isCurrentUser ? `*${winner.name}*` : winner.name;
          return `${place} ${nameDisplay} — ${winner.count}🧩`;
        });
        let winnersText = winnerLines.join('<br/>');
        if (!winnersText) winnersText = window.t('puzzle.results.no_winners');
        menu.methods.showPopupMessage({
            title: window.t('puzzle.results.title'),
            message: winnersText,
            parse_mode: 'Markdown',
            wideButtons: [
                { 
                  type: 'cancel', 
                  text: window.t('puzzle.results.button_stay') 
                },

                // Обычная кнопка (синяя) для бота
                {  type: 'ok', url: 'https://t.me/PuzzleTalesBot?start=r--bot-app-appResults', text: '🏴‍☠️ Бот Пазлыч' },

                // КРАСНАЯ кнопка для добавления в группу (добавили type: 'destructive')
                {
                  type: 'ok', 
                  // type: 'destructive', 
                  url: 'https://t.me/PuzzleTalesBot?startgroup=r--bot-app-appResults',
                  text: '➕ Добавить Пазлыча',
                },

                { 
                  type: 'destructive', 
                  id: 'close', ignore: true, text: window.t('puzzle.results.button_close') }
            ],
            buttons: [

            ],
            callback: (buttonId) => { if (buttonId === 'close') window.Telegram?.WebApp?.close(); }
        });
        work?.methods?.hostAction('puzzleResults', { winnersText: winnerLines.join('\n'), results });
    },

    clearTrayGhosts: () => {
      const { puzzle } = ref;
      const { config, state } = puzzle;
      const pieces = state?.pieces;

      let ghostCount = 0;
      let visibleInTrayCount = 0;

      Object.keys(pieces).forEach(pieceId => {
        const piece = pieces[pieceId];

        if (piece.visible) {
          visibleInTrayCount ++;
        }

        if (piece.placed && piece.visible) {
          ghostCount ++;
        }
        
      });

      if (visibleInTrayCount > 0 && (ghostCount / visibleInTrayCount) > config.ghostCleanup) {
        let clearedCount = 0;
        Object.keys(pieces).forEach(pieceId => {
          const piece = pieces[pieceId];
          if (piece && piece.placed && piece.visible) {
            piece.visible = false;
            clearedCount++;
          }
        });

        if (clearedCount > 0) {
          console.log(`Cleared ${clearedCount} ghosts from tray.`);
          ref.puzzle.methods.recalculateLayout();
          ref.puzzle.methods.redraw();
        }
      }
    },

    getCompletionPercentage: () => {
      const { puzzle, info } = ref;
      const { pieces } = puzzle.state;
      const totalPieces = Object.keys(pieces).length;
      if (totalPieces === 0) return 0;
      const localPlacedCount = Object.values(pieces).filter(p => p.placed).length;
      const serverPlacedPieces = info.game?.puzzle?.placedPieces || {};
      const serverPlacedCount = Object.keys(serverPlacedPieces).length;
      return Math.max(localPlacedCount, serverPlacedCount) / totalPieces;
    },

  }; // Конец ref.puzzle.methods

  // --- Состояние верхнего уровня Zustand ---
  return {
    puzzleRender: Date.now(),
    puzzleRedraw: Date.now(),
    updateLayout: Date.now(),
    redrawOptions: {},
    debugCounter: 0,
    pointerPosition: { x: 0, y: 0 }, // Для DraggingPiece
  };
}; // Конец createPuzzleSlice

export default createPuzzleSlice;